import type { TokenPriceMap } from "@/hooks/useTokenPrices";

/** Maps portal token symbols to keys in the useTokenPrices price map. */
const PRICE_SYMBOL_ALIASES: Record<string, string> = {
  gCOTI: "COTI",
  ETH: "WETH",
  tUSDC: "USDC.e",
};

/** Stablecoins are treated as $1 when no oracle price is available. */
const STABLECOIN_SYMBOLS = new Set(["USDT", "USDC", "USDC.e", "tUSDC"]);

export function parseBalanceAmount(balance: string): number {
  const num = parseFloat(balance.replace(/,/g, ""));
  return Number.isFinite(num) ? num : 0;
}

export function resolveTokenUsdPrice(
  symbol: string,
  prices: TokenPriceMap | null | undefined,
): number | null {
  // Direct symbol first (Sepolia/Fuji maps key by ETH/AVAX/USDC), then alias
  // (COTI maps key by WETH/USDC.e for the same assets).
  const price = prices?.[symbol] ?? prices?.[PRICE_SYMBOL_ALIASES[symbol] ?? ""];
  if (price != null && price > 0) return price;

  if (STABLECOIN_SYMBOLS.has(symbol)) return 1;

  return null;
}

/** Converts a token balance string to its USD value (balance × USD price). */
export function balanceToUsd(
  symbol: string,
  balance: string,
  prices: TokenPriceMap | null | undefined,
): number {
  const amount = parseBalanceAmount(balance);
  if (amount === 0) return 0;

  const price = resolveTokenUsdPrice(symbol, prices);
  if (price == null) return 0;

  return amount * price;
}

export function formatUsdAmount(value: number): string {
  return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Total Value formatting: amounts of a million or more drop the cents —
 * they're noise at that scale and only widen the column.
 */
export function formatUsdTotal(value: number): string {
  const fractionDigits = Math.abs(value) >= 1_000_000 ? 0 : 2;
  return value.toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export interface TokenPairUsdInput {
  symbol: string;
  publicBalance: string;
}

/** Sum USD value of all public balances (not raw token counts). */
export function sumPublicBalancesUsd(
  pairs: TokenPairUsdInput[],
  prices: TokenPriceMap | null | undefined,
): number {
  return pairs.reduce(
    (total, pair) => total + balanceToUsd(pair.symbol, pair.publicBalance, prices),
    0,
  );
}

export interface TokenPairPrivateUsdInput {
  symbol: string;
  privateBalance: string;
}

/** Sum USD value of all private balances (not raw token counts). */
export function sumPrivateBalancesUsd(
  pairs: TokenPairPrivateUsdInput[],
  prices: TokenPriceMap | null | undefined,
): number {
  return pairs.reduce(
    (total, pair) => total + balanceToUsd(pair.symbol, pair.privateBalance, prices),
    0,
  );
}
